/*
BACKFILL MIGRATION PATTERN
When adding new columns or tables that affect existing
users, always include a backfill in the migration:

Example:
ALTER TABLE tech_profiles ADD COLUMN IF NOT EXISTS
  new_feature boolean DEFAULT false;

-- Backfill existing users with sensible default:
UPDATE tech_profiles SET new_feature = false
WHERE new_feature IS NULL;

This ensures existing users are not affected by
new features added after their account was created.
*/

// Current Terms of Service version — increment for material changes only
export const CURRENT_TOS_VERSION = '2.0';

// ── Resource Directory ───────────────────────────────────────────────────────
// Feature flag — when false, ALL resource UI (tech tab, navbar link) is hidden.
// Flip to true and deploy to launch the Resource Directory.
export const SHOW_RESOURCES = true;

// Resource categories (must match the CHECK constraint in migration 071).
// Order here drives the section order on the tech Resources tab.
export const RESOURCE_CATEGORIES = [
  { id: 'regulations', label: 'Regulations & Standards',  color: '#60a5fa', bg: '#0a1a2a', border: '#60a5fa33' },
  { id: 'training',    label: 'Training & Certification', color: '#4ade80', bg: '#0a2a1a', border: '#4ade8033' },
  { id: 'reference',   label: 'Reference Documents',      color: '#d97706', bg: '#1a1408', border: '#d9770633' },
  { id: 'guides',      label: 'Industry Guides',          color: '#c084fc', bg: '#1a0a2a', border: '#c084fc33' },
];

// Resource types (must match the CHECK constraint in migration 071).
export const RESOURCE_TYPES = [
  { id: 'link',    label: 'External Link', icon: '🔗', action: 'Open' },     // 🔗
  { id: 'pdf',     label: 'PDF',           icon: '📄', action: 'Download' }, // 📄
  { id: 'article', label: 'Article',       icon: '📖', action: 'Read' },     // 📖
];

// ── Pricing Tiers ──────────────────────────────────────────────────────────
export const PRICING_TIERS = {
  starter: {
    name: 'Starter',
    monthlyPrice: 1199,
    annualPrice: 11990,
    unlocksPerMonth: 10,
    rolloverMax: 15,
    maxJobPosts: 5,
    support: 'Email support — response within 2 business days',
  },
  pro: {
    name: 'Pro',
    monthlyPrice: 1999,
    annualPrice: 19990,
    unlocksPerMonth: 15,
    rolloverMax: 25,
    maxJobPosts: Infinity,
    support: 'Priority email support — response within 1 business day',
    extras: ['Monthly account check-in'],
  },
};

export const DOD_CERT_OPTIONS = [
  "UXO Technician I", "UXO Technician II", "UXO Technician III",
  "UXO Quality Control", "UXO Safety Officer / UXOSO",
];

export const TRAVEL_OPTIONS = ["Local only", "Regional", "Nationwide", "International"];

// ── Semantic badge color tokens ──────────────────────────────────────────────
// Named tokens so badge colors are referenced by meaning, not inline hex.
// Tuned to the dark serif theme (near-black bg, amber #d97706 accent).
//   MATCH_FULL / MATCH_PARTIAL — the card's match verdict (green vs amber)
//   CERT_CHIP                  — every cert/role chip (one muted slate; text differentiates)
//   VERIFIED_ACCENT            — gold "documented" accent (hours + quality credential)
//   SUPERVISORY                — teal, supervisory hours
//   OPEN_TO_WORK               — transparent, availability text only
export const BADGE_TOKENS = {
  MATCH_FULL:      { bg: '#12351f', text: '#4ade80', border: '#22c55e' },
  MATCH_PARTIAL:   { bg: '#2a1e08', text: '#fbbf24', border: '#d97706' },
  CERT_CHIP:       { bg: '#1a1c1e', text: '#9a9490', border: '#2a2c2e' },
  VERIFIED_ACCENT: { bg: '#1a1408', text: '#d97706', border: '#d9770666' },
  SUPERVISORY:     { bg: '#0a1a1f', text: '#2dd4bf', border: '#14b8a6' },
  OPEN_TO_WORK:    { bg: 'transparent', text: '#4ade80', border: 'transparent' },
};

export const CERT_COLORS = {
  "Sweeper": "#4a4a1a",
  "UXO Technician I": "#1a4a6b",
  "UXO Technician II": "#1a6b4a",
  "UXO Technician III": "#4a1a6b",
  "UXO Quality Control": "#6b4a1a",
  "UXO Safety Officer / UXOSO": "#6b1a3a",
  "SUXOS - Senior UXO Supervisor": "#3a1a6b",
  "UXO Project Manager": "#1a3a4a",
};

export const UXO_ROLE_OPTIONS = [
  "Sweeper",
  "UXO Technician I",
  "UXO Technician II",
  "UXO Technician III",
  "UXO Quality Control",
  "UXO Safety Officer / UXOSO",
  "SUXOS - Senior UXO Supervisor",
  "UXO Project Manager",
];

export const ALL_CERT_OPTIONS = [
  "UXO Technician I", "UXO Technician II", "UXO Technician III",
  "UXO Quality Control", "UXO Safety Officer / UXOSO",
  "SUXOS - Senior UXO Supervisor", "HAZWOPER 40-Hour",
  "HAZWOPER 8-Hour Refresher", "HAZWOPER 8-Hour Supervisor",
  "10-Hour OSHA Construction Safety",
  "Current Physical", "Security Clearance",
  "Dive Certified", "Driver's License", "CDL", "EOD Background",
  "First Aid/CPR", "Quality Credential",
];
